import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import VirtualJoystick from './VirtualJoystick';
import { GAME_CONFIG } from '../config/gameConfig';

const MobileControls = ({ onDirectionChange, onAbilityPress, abilityCooldown, isAbilityActive }) => {
  const cooldownProgress = Math.min(100, (abilityCooldown / 5000) * 100);
  const abilityButtonRef = useRef(null);
  const joystickRef = useRef(null);
  
  // Calculate button size based on joystick size (85% of joystick)
  const joystickSize = GAME_CONFIG.joystickSize;
  const buttonSize = Math.floor(joystickSize * 0.85);

  const isReady = abilityCooldown === 0;

  // Multi-touch support
  useEffect(() => {
    const handleTouchStart = (e) => {
      // Allow multiple touches - one for joystick, one for ability button
      Array.from(e.touches).forEach(touch => {
        const element = document.elementFromPoint(touch.clientX, touch.clientY);

        // Check if touch is on ability button
        if (abilityButtonRef.current && abilityButtonRef.current.contains(element)) {
          if (abilityCooldown <= 0 && !isAbilityActive) {
            onAbilityPress();
          }
        }
      });
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
    };
  }, [abilityCooldown, isAbilityActive, onAbilityPress]);

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none flex items-end justify-between px-6 pb-8"
      style={{
        height: '200px',
        background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 60%, transparent 100%)'
      }}
    >
      {/* Ability Button - Left */}
      <div 
        className="flex flex-col items-center mb-1"
      >
        <div
          ref={abilityButtonRef}
          className="relative rounded-full flex items-center justify-center transition-all duration-300 pointer-events-auto"
          data-interactive="true"
          style={{
            width: `${buttonSize}px`,
            height: `${buttonSize}px`,
            opacity: isReady ? 1 : 0.5,
            backgroundColor: isReady ? '#06b6d4' : '#1f2937',
            boxShadow: isReady ? '0 0 25px rgba(6, 182, 212, 0.5)' : 'none',
            border: isReady ? 'none' : '2px solid #374151',
            transform: isAbilityActive ? 'scale(0.95)' : 'scale(1)'
          }}
        >
          {isAbilityActive ? (
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 0.5 }}
              className="text-white text-3xl"
            >
              ✋
            </motion.div>
          ) : (
            <div className="flex flex-col items-center justify-center">
              <span className={`text-sm font-bold uppercase tracking-wider ${isReady ? 'text-black' : 'text-cyan-400'}`}>
                STOP
              </span>
            </div>
          )}

          {/* Cooldown Ring - Only visible when cooling down */}
          {!isReady && (
            <svg className="absolute inset-0 w-full h-full -rotate-90">
              <circle
                cx={buttonSize / 2}
                cy={buttonSize / 2}
                r={buttonSize / 2 - 2}
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
                strokeDasharray={(buttonSize - 4) * Math.PI}
                strokeDashoffset={(buttonSize - 4) * Math.PI * (cooldownProgress / 100)}
                className="text-cyan-500 transition-all duration-100"
              />
            </svg>
          )}
        </div>
      </div>

      {/* Joystick Area - Right */}
      <div
        ref={joystickRef}
        className="pointer-events-auto"
        data-interactive="true"
      >
        <VirtualJoystick
          onDirectionChange={onDirectionChange}
          disabled={false}
        />
      </div>
    </div>
  );
};

export default MobileControls;